import { useState, useRef, useEffect } from 'react'
import { Bell } from 'lucide-react'
import { useNotifications } from '../../hooks/useNotifications'

export function NotificationBell({ isDarkMode, onNavigate }) {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications()
  const [open, setOpen] = useState(false)
  const panelRef = useRef(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const handleNotifClick = (notif) => {
    if (!notif.is_read) markAsRead(notif.id)
    if (notif.task_id && onNavigate) {
      const isClaimNotif = notif.claim_id != null || notif.type === 'claim_stage_changed'
      onNavigate(isClaimNotif ? 'claims_tasks' : 'tasks', { taskId: notif.task_id })
    }
    setOpen(false)
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`relative p-2 rounded transition-all duration-200 flex-shrink-0 ${
          isDarkMode
            ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
            : 'bg-gray-100 hover:bg-gray-200 text-sfp-darkgray'
        }`}
        aria-label="Notifications"
        title="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={`absolute right-0 mt-2 w-80 rounded-lg shadow-xl border z-50 ${
            isDarkMode
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className={`text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className={`p-6 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                No notifications
              </div>
            ) : (
              notifications.map((notif) => {
                const typeIcon =
                  notif.type === 'mention'             ? '@ ' :
                  notif.type === 'new_comment'         ? '💬 ' :
                  notif.type === 'claim_stage_changed' ? '📋 ' :
                  notif.type === 'task_reassigned'     ? '👤 ' : '🔔 '
                const unreadHighlight =
                  notif.type === 'mention'     ? (isDarkMode ? 'bg-orange-900/30' : 'bg-orange-50') :
                  !notif.is_read               ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                const dotColor =
                  notif.type === 'mention' ? 'bg-orange-500' : 'bg-blue-500'
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                      isDarkMode
                        ? `border-slate-700 hover:bg-slate-700 ${!notif.is_read ? unreadHighlight : ''}`
                        : `border-gray-50 hover:bg-gray-50 ${!notif.is_read ? unreadHighlight : ''}`
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-base flex-shrink-0 leading-tight mt-0.5">{typeIcon}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          isDarkMode ? 'text-slate-200' : 'text-gray-800'
                        }`}>
                          {notif.title}
                        </p>
                        {notif.message && (
                          <p className={`text-xs mt-0.5 truncate ${
                            isDarkMode ? 'text-slate-400' : 'text-gray-500'
                          }`}>
                            {notif.message}
                          </p>
                        )}
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                          {new Date(notif.created_at).toLocaleString('en-ZA')}
                        </p>
                      </div>
                      {!notif.is_read && (
                        <div className={`flex-shrink-0 w-2 h-2 mt-1 rounded-full ${dotColor}`} />
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>

          {/* Footer */}
          <div className={`px-4 py-2 border-t text-center ${
            isDarkMode ? 'border-slate-700' : 'border-gray-100'
          }`}>
            <button
              onClick={() => { onNavigate?.('claims_tasks'); setOpen(false) }}
              className={`text-xs ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
            >
              View Dispatch Claims →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
